/**
 * The screen shown before there is a workspace to show.
 *
 * Sign-in, sign-in failure, the local account picker and the two session states
 * were four near-copies of the same centred card. One component means a change
 * to the frame lands on all of them.
 *
 * @module components/shell/AuthScreen
 */

import React, { type ReactNode } from "react";

import { BrandWordmark } from "@/components/shared/BrandWordmark";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { Heading, MutedText, Panel, Topbar } from "@/components/ui";

import { composePageTitle } from "./page-title";
import { useDocumentTitle } from "./RouteTitle";

export interface AuthScreenProps {
  title: string;
  /** One line under the heading, saying what is happening or what to do. */
  message?: string;
  /** The form, the alert or the button this screen exists for. */
  children?: ReactNode;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <AuthScreen title="Sign in to your workspace" message="Use your organisation account.">
 *   <Button variant="primary" onClick={login}>Sign in with Microsoft</Button>
 * </AuthScreen>
 * ```
 */
export function AuthScreen({
  title,
  message = undefined,
  children = undefined,
  "data-testid": testId,
}: AuthScreenProps): React.ReactElement {
  // These screens render before there is a router to name them, so the one
  // heading they already carry is what the tab reads.
  useDocumentTitle(composePageTitle([title]));
  return (
    <div className="flex min-h-[100dvh] flex-col bg-base-200 text-base-content">
      <Topbar
        navigation={false}
        brand={<BrandWordmark size="wordmark" />}
        utilities={<ThemeToggle data-testid="entra-theme-toggle" />}
      />
      <main className="mx-auto flex w-full max-w-md flex-1 items-center px-4 py-10 sm:px-6">
        <Panel className="w-full" data-testid={testId}>
          <Heading level={1} size="lg">
            {title}
          </Heading>
          {message && (
            <MutedText className="mt-3" measure>
              {message}
            </MutedText>
          )}
          {children}
        </Panel>
      </main>
    </div>
  );
}
